// src/pages/stock/AddStockCode.tsx
//
// Add Stock Code
//
// Page version of the legacy "AddStockCodeModalContent" (Modal -> Page).
// The route handle provides AppPageHeader title/description/back; this page does not inject header actions.
//
// Behavior summary
// - Stock Code must be verified for availability before creation.
// - "Create" is enabled only when:
//   - required fields are filled,
//   - Stock Code is verified as AVAILABLE,
//   - and no async operation is in progress.
// - Uses shared confirm modal + systemAlert for user feedback.
// - Uses MUI components only (no custom UI components).

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Add as AddIcon } from '@mui/icons-material';

import { EsimType, StockStatus } from '@models/stock';
import type { StockUpsertRequest } from '@apis/stock';
import { createStock, getStockByStockCode } from '@apis/stock';

import { useRegionList } from '@queries/regions/useRegionList';
import { useAllSupplierList } from '@queries/suppliers/useAllSupplierList';

import { ESIM_TYPE_OPTION_LIST } from '@constants/stocks';
import { dropEmptyValues } from '@utils/common';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type StockCodeCheckStatus = 'NOT_CHECKED' | 'IN_USE' | 'AVAILABLE';

type Option<T> = { label: string; value: T };

const PAGE_MAX_WIDTH = 900;

const normalize = (v?: string | null) => (v ?? '').trim();

const isPositiveInteger = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 && Number.isInteger(n);

const getStatusChip = (status: StockCodeCheckStatus) => {
  switch (status) {
    case 'AVAILABLE':
      return <Chip size="small" color="success" label="Available" sx={{ fontWeight: 900 }} />;
    case 'IN_USE':
      return <Chip size="small" color="error" label="Already in use" sx={{ fontWeight: 900 }} />;
    default:
      return <Chip size="small" variant="outlined" label="Not checked" sx={{ fontWeight: 900 }} />;
  }
};

const AddStockCode = () => {
  const navigate = useNavigate();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const [form, setForm] = React.useState<StockUpsertRequest>({
    stockCode: '',
    stockName: '', // UI label: "Stock Label"
    regionId: 0,
    supplierId: 0,
    isUsingApi: false,
    status: StockStatus.ACTIVE,
    esimType: EsimType.LPA,
  });

  const [stockCodeStatus, setStockCodeStatus] = React.useState<StockCodeCheckStatus>('NOT_CHECKED');
  const [isChecking, setIsChecking] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);

  const isBusy = isChecking || isCreating;

  // External options
  const { data: regionsData, isLoading: isRegionsLoading } = useRegionList();
  const { data: suppliersData, isLoading: isSuppliersLoading } = useAllSupplierList();

  const regionOptions = React.useMemo<Array<Option<number>>>(() => {
    return (regionsData?.data ?? []).map((r) => ({
      label: r.regionCode,
      value: r.regionId,
    }));
  }, [regionsData]);

  const supplierOptions = React.useMemo<Array<Option<number>>>(() => {
    const list = (suppliersData?.data ?? []).map((s) => ({
      label: `${s.supplierName} (${s.supplierCode})`,
      value: s.supplierId,
    }));
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [suppliersData]);

  const selectedRegion = React.useMemo(() => {
    if (!form.regionId) return null;
    return regionOptions.find((o) => o.value === form.regionId) ?? null;
  }, [form.regionId, regionOptions]);

  const selectedSupplier = React.useMemo(() => {
    if (!form.supplierId) return null;
    return supplierOptions.find((o) => o.value === form.supplierId) ?? null;
  }, [form.supplierId, supplierOptions]);

  // Stock code changes always invalidate the previous verification result.
  const handleChangeStockCode = (stockCode: string) => {
    setStockCodeStatus('NOT_CHECKED');
    setForm((prev) => ({ ...prev, stockCode }));
  };

  const handleChangeStockLabel = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, stockName: e.target.value }));
  };

  const handleChangeTopUpValidityDays = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    setForm((prev) => ({
      ...prev,
      topUpValidityDays: Number.isFinite(n) && n > 0 ? n : undefined,
    }));
  };

  const handleChangeEsimType = (e: SelectChangeEvent<unknown>) => {
    setForm((prev) => ({ ...prev, esimType: e.target.value as any }));
  };

  const handleToggleActive = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      status: checked ? StockStatus.ACTIVE : StockStatus.INACTIVE,
    }));
  };

  const handleToggleCarrierApi = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      isUsingApi: checked,
      // Only keep topUpValidityDays when API integration is enabled.
      topUpValidityDays: checked ? prev.topUpValidityDays : undefined,
    }));
  };

  const handleVerifyAvailability = React.useCallback(async () => {
    const code = normalize(form.stockCode);
    if (!code) {
      systemAlert.info('Please enter a Stock Code first.');
      return;
    }
    if (isBusy) return;

    try {
      setIsChecking(true);

      const { data } = await getStockByStockCode(code);
      const exists = (data ?? []).length > 0;

      if (exists) {
        setStockCodeStatus('IN_USE');
        systemAlert.error('This Stock Code is already in use.');
      } else {
        setStockCodeStatus('AVAILABLE');
        systemAlert.success('This Stock Code is available.');
      }
    } catch {
      setStockCodeStatus('NOT_CHECKED');
      systemAlert.error('Failed to verify availability. Please try again.');
    } finally {
      setIsChecking(false);
    }
  }, [form.stockCode, isBusy, systemAlert]);

  const isFormFilled = React.useMemo(() => {
    if (!normalize(form.stockCode)) return false;
    if (!normalize(form.stockName)) return false;
    if (!form.regionId) return false;
    if (!form.supplierId) return false;
    if (!form.esimType) return false;

    if (form.isUsingApi) {
      if (!isPositiveInteger(form.topUpValidityDays)) return false;
    }

    return true;
  }, [form]);

  const canCreate = isFormFilled && stockCodeStatus === 'AVAILABLE' && !isBusy;

  const availabilityHelperText = React.useMemo(() => {
    const code = normalize(form.stockCode);

    if (!code) return 'Enter a Stock Code, then click Verify to check availability.';
    if (stockCodeStatus === 'AVAILABLE') return 'This Stock Code is available.';
    if (stockCodeStatus === 'IN_USE') return 'This Stock Code is already in use. Please choose a different one.';
    return 'Please verify availability before creating.';
  }, [form.stockCode, stockCodeStatus]);

  const availabilityHelperColor = React.useMemo(() => {
    const code = normalize(form.stockCode);

    if (!code) return 'text.secondary';
    if (stockCodeStatus === 'AVAILABLE') return 'success.main';
    if (stockCodeStatus === 'IN_USE') return 'error.main';
    return 'text.secondary';
  }, [form.stockCode, stockCodeStatus]);

  const handleCancel = React.useCallback(async () => {
    if (isBusy) return;

    const ok = await confirm({
      priority: 'info',
      title: 'Cancel?',
      description: 'Leave this page and discard what you entered?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [confirm, isBusy, navigate]);

  const handleCreate = React.useCallback(async () => {
    if (!canCreate) return;

    const ok = await confirm({
      priority: 'info',
      title: 'Create Stock Code',
      description: (
        <>
          Create this Stock Code?
          <br />
          <b>{normalize(form.stockCode) || '-'}</b>
        </>
      ),
      confirmText: 'Create',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      setIsCreating(true);

      // Keep API contract: topUpValidityDays is optional unless API integration is enabled.
      const payload = dropEmptyValues(form, ['topUpValidityDays']);
      await createStock(payload);

      systemAlert.success('Stock Code created successfully.');
      navigate('/stocks/codes', { replace: true });
    } catch {
      systemAlert.error('Failed to create Stock Code. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [canCreate, confirm, form, navigate, systemAlert]);

  return (
    <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
      {/* Title/description/back are rendered by AppPageHeader (route handle). */}

      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ mt: 2, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            {/* Stock Code + availability verification */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="subtitle2" fontWeight={900}>
                  Stock Code
                </Typography>
                {getStatusChip(stockCodeStatus)}
              </Stack>

              <TextField
                label="Stock Code"
                value={form.stockCode}
                onChange={(e) => handleChangeStockCode(e.target.value)}
                fullWidth
                disabled={isBusy}
                placeholder="Enter the code, then verify availability"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Verify availability">
                        <span>
                          <Button
                            onClick={handleVerifyAvailability}
                            size="small"
                            variant="outlined"
                            startIcon={isChecking ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
                            disabled={isBusy || !normalize(form.stockCode)}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Verify
                          </Button>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  color: availabilityHelperColor,
                  fontWeight: 700,
                }}
              >
                {availabilityHelperText}
              </Typography>
            </Box>

            <Divider />

            {/* Stock Label */}
            <TextField
              label="Stock Label"
              value={form.stockName}
              onChange={handleChangeStockLabel}
              fullWidth
              disabled={isBusy}
              placeholder="Enter a label for this Stock Code"
            />

            {/* Region */}
            <Autocomplete
              options={regionOptions}
              value={selectedRegion}
              onChange={(_, opt) => setForm((prev) => ({ ...prev, regionId: opt?.value ?? 0 }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={isRegionsLoading}
              disabled={isBusy || isRegionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Region"
                  placeholder="Type to filter regions"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isRegionsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Product Type */}
            <FormControl fullWidth disabled={isBusy}>
              <InputLabel id="esim-type-label">Product Type</InputLabel>
              <Select
                labelId="esim-type-label"
                label="Product Type"
                value={form.esimType}
                onChange={handleChangeEsimType}
              >
                {ESIM_TYPE_OPTION_LIST.map((o) => (
                  <MenuItem key={String(o.value)} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Supplier */}
            <Autocomplete
              options={supplierOptions}
              value={selectedSupplier}
              onChange={(_, opt) => setForm((prev) => ({ ...prev, supplierId: opt?.value ?? 0 }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={isSuppliersLoading}
              disabled={isBusy || isSuppliersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  placeholder="Type to filter suppliers"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSuppliersLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Divider />

            {/* Status toggles */}
            <Stack spacing={1.25}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch checked={form.status === StockStatus.ACTIVE} onChange={handleToggleActive} disabled={isBusy} />
                  }
                  label="Active"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 5 }}>
                  Enables or disables this Stock Code.
                </Typography>
              </Box>

              <Box>
                <FormControlLabel
                  control={<Switch checked={form.isUsingApi} onChange={handleToggleCarrierApi} disabled={isBusy} />}
                  label="Carrier API Integration"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 5 }}>
                  Enables carrier API integration for this Stock Code.
                </Typography>
              </Box>
            </Stack>

            {/* API-only field */}
            {form.isUsingApi && (
              <TextField
                label="Top-up Validity (Days)"
                value={form.topUpValidityDays ?? ''}
                onChange={handleChangeTopUpValidityDays}
                fullWidth
                disabled={isBusy}
                placeholder="Enter the top-up validity in days"
                type="number"
                inputProps={{ min: 1 }}
              />
            )}

            <Divider />

            {/* Footer actions */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleCancel} disabled={isBusy} sx={{ whiteSpace: 'nowrap' }}>
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={!canCreate}
                startIcon={isCreating ? <CircularProgress size={18} /> : <AddIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isCreating ? 'Creating…' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default AddStockCode;
